import { useId, type FC, type ReactElement, type ReactNode } from "react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";

import { ChartContainer, ChartTooltip, type ChartConfig } from "@/components/flexkit/charts/Chart";
import {
    AREA_GRADIENT_STOPS,
    CARTESIAN_GRID_DEFAULTS,
    CHART_ANIMATION_ACTIVE,
    CHART_HEIGHTS,
    CHART_MARGIN_DEFAULTS,
    XAXIS_DEFAULTS,
    YAXIS_DEFAULTS,
    type ChartHeightTier,
} from "@/components/flexkit/charts/chart-defaults";
import { useChartBrushZoom } from "@/components/flexkit/charts/useChartBrushZoom";
import { cn } from "@/components/podkit/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type AreaSeriesProps = {
    /** Data key for this series. */
    dataKey: string;
    /** Stroke color. Falls back to the ChartConfig color. */
    color?: string;
    /** Line interpolation type. @default "linear" */
    type?: "linear" | "monotone" | "step" | "basis" | "natural";
    /** Stroke width. @default 2 */
    strokeWidth?: number;
};

type BaseAreaChartProps = {
    data: Record<string, unknown>[];
    config: ChartConfig;
    /** Height tier or pixel value. @default "md" */
    height?: ChartHeightTier | number;
    /** Data key for the X axis. @default "date" */
    xAxisDataKey?: string;
    /** X axis type. @default "category" */
    xAxisType?: "category" | "number";
    /** X axis scale. */
    xAxisScale?: "auto" | "linear" | "time";
    /** X axis domain. */
    xAxisDomain?: [string | number, string | number];
    /** X axis padding. */
    xAxisPadding?: { left?: number; right?: number };
    /** X axis tick count. */
    xAxisTickCount?: number;
    /** Custom X axis tick formatter. */
    xAxisTickFormatter?: (value: unknown) => string;
    /** Formatter for Y axis tick labels. */
    yAxisFormatter?: (value: number) => string;
    /** Y axis width in px. */
    yAxisWidth?: number;
    /** Y axis domain. @default [0, "auto"] */
    yAxisDomain?: [number | string, number | string];
    /** Y axis min tick gap. */
    yAxisMinTickGap?: number;
    /** Allow decimals on Y axis. @default false */
    yAxisAllowDecimals?: boolean;
    /** Minimum gap between X axis ticks. @default 32 */
    xAxisMinTickGap?: number;
    /** Custom tooltip content element. */
    tooltipContent?: ReactElement;
    /** Optional legend element rendered after the chart. */
    legend?: ReactNode;
    /** Override default chart margins. */
    margin?: { top?: number; right?: number; bottom?: number; left?: number };
    className?: string;
    /**
     * Area series definitions. Each entry renders an `<Area>`.
     * The gradient fill is auto-generated from the config color.
     */
    series: AreaSeriesProps[];
    onBrushZoom?: (x1: string | number, x2: string | number) => void;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BaseAreaChart: FC<BaseAreaChartProps> = ({
    data,
    config,
    height = "md",
    xAxisDataKey = "date",
    xAxisType,
    xAxisScale,
    xAxisDomain,
    xAxisPadding,
    xAxisTickCount,
    xAxisTickFormatter,
    yAxisFormatter,
    yAxisWidth,
    yAxisDomain = [0, "auto"],
    yAxisMinTickGap,
    yAxisAllowDecimals = false,
    xAxisMinTickGap = 32,
    tooltipContent,
    legend,
    margin,
    className,
    series,
    onBrushZoom,
}) => {
    const idPrefix = useId().replace(/:/g, "");
    const heightPx = typeof height === "number" ? height : CHART_HEIGHTS[height];
    const zoom = useChartBrushZoom({ onZoom: onBrushZoom });
    const isSinglePoint = data.length === 1;

    return (
        <ChartContainer
            config={config}
            className={cn("w-full", onBrushZoom && "cursor-crosshair", zoom.isDragging && "select-none", className)}
            style={{ height: heightPx }}
        >
            <AreaChart
                data={data}
                margin={{ ...CHART_MARGIN_DEFAULTS, ...margin }}
                {...(onBrushZoom ? zoom.chartEvents : {})}
            >
                <defs>
                    {series.map((s) => {
                        const color = s.color ?? `var(--color-${s.dataKey})`;
                        return (
                            <linearGradient key={s.dataKey} id={`${idPrefix}-${s.dataKey}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={color} stopOpacity={AREA_GRADIENT_STOPS.topOpacity} />
                                <stop offset="95%" stopColor={color} stopOpacity={AREA_GRADIENT_STOPS.bottomOpacity} />
                            </linearGradient>
                        );
                    })}
                </defs>
                <CartesianGrid {...CARTESIAN_GRID_DEFAULTS} />
                <XAxis
                    dataKey={xAxisDataKey}
                    {...XAXIS_DEFAULTS}
                    type={xAxisType}
                    scale={xAxisScale}
                    domain={xAxisDomain}
                    padding={xAxisPadding}
                    tickCount={xAxisTickCount}
                    minTickGap={xAxisMinTickGap}
                    tickFormatter={xAxisTickFormatter}
                />
                <YAxis
                    {...YAXIS_DEFAULTS}
                    tickFormatter={yAxisFormatter}
                    width={yAxisWidth}
                    domain={yAxisDomain}
                    minTickGap={yAxisMinTickGap}
                    allowDecimals={yAxisAllowDecimals}
                />
                <ChartTooltip cursor={false} content={tooltipContent} />
                {series.map((s) => {
                    const color = s.color ?? `var(--color-${s.dataKey})`;
                    return (
                        <Area
                            key={s.dataKey}
                            type={s.type ?? "linear"}
                            dataKey={s.dataKey}
                            stroke={color}
                            fill={`url(#${idPrefix}-${s.dataKey})`}
                            strokeWidth={s.strokeWidth ?? 2}
                            dot={isSinglePoint ? { r: 4, fill: color, stroke: color } : false}
                            isAnimationActive={CHART_ANIMATION_ACTIVE}
                        />
                    );
                })}
                {legend}
                {zoom.referenceArea}
            </AreaChart>
        </ChartContainer>
    );
};
